import { Duration, formatDuration, intervalToDuration } from "date-fns";
import { Card, CardTimer } from "@/types/v2";
import { firestoreToDateTime } from "@/lib/utils";

// Relocated verbatim from Card.Metrics.tsx. Chooses the two most-significant
// non-zero units to display. Do NOT alter the branching — the card metrics use
// raw wall-clock math and this formatting is part of that contract.
export const customFormatDuration = (duration: Duration): string => {
  if (
    !duration.years &&
    !duration.months &&
    !duration.days &&
    !duration.hours
  ) {
    return formatDuration(duration, { format: ["minutes", "seconds"] });
  } else if (!duration.years && !duration.months && !duration.days) {
    if (!duration.minutes) {
      return formatDuration(duration, { format: ["hours", "seconds"] });
    }
    return formatDuration(duration, { format: ["hours", "minutes"] });
  } else if (!duration.years && !duration.months) {
    if (!duration.hours && !duration.minutes)
      return formatDuration(duration, { format: ["days", "seconds"] });
    else if (!duration.hours)
      return formatDuration(duration, { format: ["days", "minutes"] });
    return formatDuration(duration, { format: ["days", "hours"] });
  } else if (!duration.years) {
    if (!duration.days && !duration.hours && !duration.minutes)
      return formatDuration(duration, { format: ["months", "seconds"] });
    else if (!duration.days && !duration.hours)
      return formatDuration(duration, { format: ["months", "minutes"] });
    else if (!duration.days)
      return formatDuration(duration, { format: ["months", "hours"] });
    return formatDuration(duration, { format: ["months", "days"] });
  }

  return formatDuration(duration, { format: ["years", "months"] });
};

// Raw wall-clock duration a card spent in a single container. Relocated
// verbatim from Card.Metrics.tsx — no working-hours clamping (see the
// container-metrics calculation for the shop-hours-aware variant; unifying
// them is deliberately out of scope).
export const getContainerDuration = (containerInfo: CardTimer): Duration => {
  const enteredAt = firestoreToDateTime(containerInfo.enteredAt) as Date; // enteredAt is always defined
  const exitedAt = firestoreToDateTime(
    containerInfo.exitedAt ?? new Date()
  ) as Date; // fall back to now for the current container
  return intervalToDuration({
    start: enteredAt,
    end: exitedAt,
  });
};

// Raw wall-clock time from the card's first container entry until now.
// NOTE (preserved quirk): this same value is displayed as both "Total
// Container Time" and "Total Time in Shop" — "Total Container Time" is not
// independently computed. Relocated verbatim; do not "fix".
export const getTotalTimeInShop = (history: Card["history"]): string => {
  if (!history || history.length === 0) return "No history available";

  const enteredAt = firestoreToDateTime(history[0].enteredAt) as Date; // enteredAt is always defined
  const currentDateTime = new Date();
  const duration = intervalToDuration({
    start: enteredAt,
    end: currentDateTime,
  });
  return customFormatDuration(duration);
};

// Sums each container's raw wall-clock durations across every visit, keyed by
// container id. Relocated verbatim from Card.Metrics.tsx's AggregateCardHistory;
// container-name resolution is left to the caller. The manual carry logic
// (60s→1m, 60m→1h, 24h→1d, 31d→1mo, 12mo→1y) is preserved exactly.
export const getAggregatedContainerDurations = (
  history: Card["history"]
): { containerId: string; totalDuration: Duration }[] => {
  const historicalContainers = [
    ...new Set(
      history.map((containerInfo) => containerInfo.currentContainerId)
    ),
  ];
  return historicalContainers.map((containerId) => {
    const containerHistory =
      history.filter(
        (containerInfo) => containerInfo.currentContainerId === containerId
      ) ?? [];
    const totalDuration = containerHistory.reduce<Duration>(
      (acc, curr) => {
        const duration = getContainerDuration(curr);
        const newDuration = {
          seconds: (acc.seconds || 0) + (duration.seconds || 0),
          minutes: (acc.minutes || 0) + (duration.minutes || 0),
          hours: (acc.hours || 0) + (duration.hours || 0),
          days: (acc.days || 0) + (duration.days || 0),
          months: (acc.months || 0) + (duration.months || 0),
          years: (acc.years || 0) + (duration.years || 0),
        };
        if (newDuration.seconds >= 60) {
          newDuration.minutes = (newDuration.minutes || 0) + 1;
          newDuration.seconds = newDuration.seconds - 60;
        }
        if (newDuration.minutes >= 60) {
          newDuration.hours = (newDuration.hours || 0) + 1;
          newDuration.minutes = newDuration.minutes - 60;
        }
        if (newDuration.hours >= 24) {
          newDuration.days = (newDuration.days || 0) + 1;
          newDuration.hours = newDuration.hours - 24;
        }
        if (newDuration.days >= 31) {
          newDuration.months = (newDuration.months || 0) + 1;
          newDuration.days = newDuration.days - 31;
        }
        if (newDuration.months >= 12) {
          newDuration.years = (newDuration.years || 0) + 1;
          newDuration.months = newDuration.months - 12;
        }
        return newDuration;
      },
      {
        years: 0,
        months: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      }
    );

    return { containerId, totalDuration };
  });
};
